/* eslint-disable camelcase */
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CircularProgress, Grid, Box } from "@mui/material";
import { THEME_MAP, ThemeOrgName } from "@/store/middleware/themeKeyMiddleware";
import { updateUserSetting, userPermissionsSelector, userSelector } from "@/store/slices/userSlice";
import { userType, SERVICE_INVOICE_PAID_BY_TYPES } from "@/constants";
import ConfirmModal from "@/components/customComponents/ConfirmModal";
import OrganisationAclDetailsSection from "./OrganisationAclDetailsSection";
import OrganisationDetailsSection from "./OrganisationDetailsSection";
import UserDetailsSection from "./UserDetailsSection";
import { useAccessAppComponent } from "../../../hooks/useAccessAppComponent";

const UserDetails = () => {
  const dispatch = useDispatch();
  const { accessComponents } = useAccessAppComponent();
  const { user: userAccessComponent, organisation: orgAccessComponent } = accessComponents;
  const userPermission = useSelector(userPermissionsSelector);
  const isOrgEditable = userPermission?.org?.edit;
  const userState = useSelector(userSelector);
  const { _id, service_invoice_paid_by } = userState;

  const [isOpen, setIsOpen] = useState(false);
  const [userSetting, setUserSetting] = useState(service_invoice_paid_by || "");

  const isInvoicePaidByIsDifferent = userState?.service_invoice_paid_by !== userState?.org_service_invoice_paid_by;
  const isUsersUseOrgInvoicePaidBy = userState?.is_users_use_org_invoice_paid_by;
  const isOwnerUser = userState?.userType === userType.owner;
  const isDisableInvoicePaidBy = !isOwnerUser && !isInvoicePaidByIsDifferent;

  let ServiceInvoicePaidByInfo = "";
  if (!isOwnerUser && isInvoicePaidByIsDifferent) {
    ServiceInvoicePaidByInfo =
      "The invoice paid by is different from the organisation setting. You can update it to align with the organisation setting.";
  } else if (
    isOwnerUser &&
    userState?.service_invoice_paid_by === SERVICE_INVOICE_PAID_BY_TYPES.USER &&
    isUsersUseOrgInvoicePaidBy
  ) {
    ServiceInvoicePaidByInfo = "Billing for your organisation's users will continue to be managed under your account";
  }

  useEffect(() => {
    if (service_invoice_paid_by) {
      setUserSetting(service_invoice_paid_by);
    }
  }, [service_invoice_paid_by]);

  const handlePaidBy = (event) => {
    setUserSetting(event.target.value);
    if (userState?.userType === userType.owner) {
      setIsOpen({ isOpen: true });
    } else {
      dispatch(
        updateUserSetting({
          serviceInvoicePaidBy: event.target.value,
          isUpdateAllUserSettings: false,
        }),
      );
    }
  };

  const handleUpdateInvoicePaidBy = (data) => {
    dispatch(
      updateUserSetting({
        serviceInvoicePaidBy: userSetting,
        isUpdateAllUserSettings: data.isSave,
      }),
    );
    setIsOpen(false);
  };

  return (
    <>
      <Grid container spacing={1.5}>
        {userState?._id ? (
          <>
            <UserDetailsSection
              userState={userState}
              dispatch={dispatch}
              userId={_id}
              userSetting={userSetting}
              onPaidByChange={handlePaidBy}
              isDisableInvoicePaidBy={isDisableInvoicePaidBy}
              serviceInvoicePaidByInfo={ServiceInvoicePaidByInfo}
              userAccessComponent={userAccessComponent}
            />
            <OrganisationDetailsSection
              organisation={userState?.organisation}
              orgId={userState?.organisation?._id}
              dispatch={dispatch}
              isOrgEditable={isOrgEditable}
              orgAccessComponent={orgAccessComponent}
            />
            <OrganisationAclDetailsSection
              organisation={userState?.organisation}
              dispatch={dispatch}
              isOrgEditable={isOrgEditable}
              orgAccessComponent={orgAccessComponent}
            />
          </>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignContent: "center",
              justifyContent: "center",
              width: "100%",
              marginTop: "50px",
            }}
          >
            <CircularProgress size={40} />
          </Box>
        )}
      </Grid>
      <ConfirmModal
        isOpen={isOpen}
        handle={handleUpdateInvoicePaidBy}
        setIsOpen={setIsOpen}
        title="Confirm"
        btnTextNo="No"
        btnTextYes="Yes"
        confirmLoadingText="Updating..."
        message="Are you sure you want to update your organisation's user invoice paid by?"
      />
    </>
  );
};

export default UserDetails;
